import { lazy, Suspense } from "react";
import { Routes, Route } from "react-router";
import Home from "./pages/Home";
import { ScrollToTop } from "./components/ScrollToTop";
import { ThemeProvider } from "./providers/theme";

// Marketing pages — split so Home LCP doesn't download the authenticated shell.
const About = lazy(() => import("./pages/About"));
const Careers = lazy(() => import("./pages/Careers"));
const Contact = lazy(() => import("./pages/Contact"));
const Customers = lazy(() => import("./pages/Customers"));
const Industries = lazy(() => import("./pages/Industries"));
const IndustryDetail = lazy(() => import("./pages/IndustryDetail"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Product = lazy(() => import("./pages/Product"));
const AgentBlueprint = lazy(() => import("./pages/AgentBlueprint"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const Disclaimer = lazy(() => import("./pages/Disclaimer"));
const NotInvited = lazy(() => import("./pages/NotInvited"));

// tRPC context for every non-Home route (marketing forms + portal).
// Kept off Home's critical path via lazy import + vite modulePreload filter.
const TrpcShell = lazy(() => import("./providers/TrpcShell"));

// Portal / login / authenticated product UI — never on the marketing critical path.
const AuthenticatedApp = lazy(() => import("./AuthenticatedApp"));

function RouteFallback() {
  return (
    <div
      className="min-h-screen"
      style={{ background: "var(--surface-page)" }}
      aria-busy="true"
      aria-label="Loading"
    />
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <ScrollToTop />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Home stays outside TrpcShell so first paint skips tRPC/react-query. */}
          <Route path="/" element={<Home />} />
          <Route element={<TrpcShell />}>
            <Route path="/about" element={<About />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/customers" element={<Customers />} />
            <Route path="/industries" element={<Industries />} />
            <Route path="/industries/:slug" element={<IndustryDetail />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/product" element={<Product />} />
            <Route path="/agent-blueprint" element={<AgentBlueprint />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/disclaimer" element={<Disclaimer />} />
            <Route path="/not-invited" element={<NotInvited />} />
            {/* Login, portal, and authenticated product UI */}
            <Route path="*" element={<AuthenticatedApp />} />
          </Route>
        </Routes>
      </Suspense>
    </ThemeProvider>
  );
}
